import { deleteLogicUser, reactiveUser, restorePasswordEmailUser } from "../api/usersAPI";
import { useState } from "react";
import { UserListFields } from '../types';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { setSearchText } from "../../../../redux/historicalSlice";
import { Button, Row, Col, Spinner, Form } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { useUser } from "../../../../utils/auth";
import Search from "../../../../components/Search";
import { setFilterByOnlyActiveRecords } from "../../../../redux/filtersSlice";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { useModal } from "../../../../hooks/useModal";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { DeleteReasonDto } from "../../../../types/DeleteReasonDto";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { SendEmailDto } from "../../generic/types";
import { formatDate } from "../../../../utils/formatters/DateFormatter";
import { useSearchUsers } from "../hooks/useSearchUsers";

export default function User() {
    const { t } = useTranslation();
    const userInfo = useUser(); 
    const hasClient = userInfo.userInfo.idClient != null; 
    const navigate = useNavigate();
    const [motiveDelete, setMotiveDelete] = useState('');

    const dispatch = useDispatch();
    const currentPage = useSelector((state: RootState) => state.historical.user.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.user.itemsPerPage);
    const currentUser = useSelector((state: RootState) => state.currentUserInfo.data);
    const [searchTerm, setSearchTerm] = useState('');
    
    const { data: usersData, isLoading, refetch } = useSearchUsers();

    const handleSearchInput = () => {
        dispatch(setSearchText({ table: "user", text: searchTerm, goFirstPage: true }));
    };

    const handleClear = () => {
        setSearchTerm("");
        dispatch(setSearchText({ table: "user", text: '', goFirstPage: true }));
    }

    const [messageError, setMessageError] = useState<string | undefined>('');

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch(setFilterByOnlyActiveRecords({ table: "users", filterByOnlyActiveRecords: e.target.checked }));
    };
    
    const onlyActiveToggle = useSelector((state: RootState) => state.filters.users.filterByOnlyActiveRecords);

    const columns: ColumnConfig<UserListFields>[] = [
            { header: t("user:usersListHeader1"), key: "name", render: (item) => <span>{item.name} {item.surname} {item.surname2} </span>},
            { header: t("user:usersListHeader2"), key: "email"},
            { header: t("user:usersListHeader3"), key: "rol"},
            {
                header: t("role:table.status"),
                key: "status",
                render: (item) => (
                    <span className={`status-tag ${item.deletedDate == null ? "status-tag--active" : "status-tag--disabled"}`}>
                        {item.deletedDate == null
                            ? t("role:table.statusActive")
                            : t("role:table.statusDisable")}
                    </span>
                )
            },
            { header: t("user:usersListHeader4"), key: "dataCreacio", render: (item) => <span>{formatDate(item.dataCreacio)} </span>},

        ];

    const { showModal, modalData, endDate, openModal, closeModal, setLoading, currentItem } = useModal<UserListFields>();
    const { alert, showAlert, hideAlert, isVisible } = useAlert({ fromLocation: true });

    const USERS_MODAL_HEADERS = {
        titleDelete: t("user:modal.modalDeleteUserTitle"),
        subtitleDelete: t("user:modal.modalDeleteUserSubtitle"),
        titleRestore: t("user:modal.modalRestoreUserTitle"),
        subtitleRestore: t("user:modal.modalRestoreUserSubtitle")
    };

    const handleDelete = async (user: UserListFields) => {
        openModal(
            user,
            USERS_MODAL_HEADERS.titleDelete,
            `${USERS_MODAL_HEADERS.subtitleDelete} ${ user.name}?`,
            "delete",
            ""
        );
    };

    const handleRestore = async (user: UserListFields) => {
        openModal(
            user,
            USERS_MODAL_HEADERS.titleRestore,
            `${USERS_MODAL_HEADERS.subtitleRestore} ${ user.name}?`,
            "restore",
            ""
        );
    };

     const confirmSubmitModal = async () => {
        setLoading(true);
        if ( modalData.type == 'delete' && motiveDelete != "") {

            try {
                console.log("motiveDelete", motiveDelete)
                
                const body: DeleteReasonDto = {
                    deleteReason: motiveDelete 
                };
                await deleteLogicUser(currentItem!.id, body)
                closeModal();
                await refetch();
                showAlert({
                    message: t("user:modal.messageDeleteUserSuccessfully"),
                    type: 'success',
                    duration: 8000
                });
            } catch (error) {
                const fetchError = error as ApiErrorResponse;
                const errorMessage = fetchError?.message
                closeModal();
                showAlert({
                    message: errorMessage?.toString() ?? t("user:modal.messageDeleteUserError"),
                    type: 'danger',
                    duration: 8000
                });
            } finally{
                setLoading(false);
            }
        }
        if (modalData.type == "restore") {
            try {
                const body: SendEmailDto = {
                    idUser: currentItem!.id,
                    name: currentItem!.name,
                    email: currentItem!.email,
                    emailType: "restore",
                    language: ""
                }
                await restorePasswordEmailUser(body);
                showAlert({
                    message: t("user:modal.messageRestoreUserSuccessfully"),
                    type: 'success',
                    duration: 8000
                });
                closeModal();

            } catch (error) {
                const fetchError = error as ApiErrorResponse;
                closeModal();
                showAlert({
                    message: fetchError?.message ?? t("user:modal.messageRestoreUserError"),
                    type: 'danger',
                    duration: 8000
                });

            } finally {
                setLoading(false);
            }
        }
        if (modalData.type === "active") {
            try {
                await reactiveUser(currentItem!.id);
                await refetch();
                closeModal();
                showAlert({
                    message: t("user:modal.messageReactivateUserSuccessfully"),
                    type: 'success',
                    duration: 8000
                });
            } catch (error) {
                const fetchError = error as ApiErrorResponse;
                closeModal();
                if(fetchError?.message == "ERROR_USER_ACTIVATE_EXISTS"){
                    showAlert({
                        message: fetchError?.message ?? t("user:modal.messageReactivateUserError"),
                        type: 'danger',
                        duration: 8000
                    });
                }
            }
        }
    }

    const handleMotiveDeleteChange = async (value: string | null) => {
        setMotiveDelete(value || '');
    }

    const handleActive = (user: UserListFields) => {
        openModal(
            user,
            t("genericDic:modal.modalHeaderActiveTitle"),
            t("genericDic:modal.modalHeaderActiveSubtitle", {description: user.name}),
            "active",
            ""
        );
    };
    return (
        <>
            {isLoading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {messageError && <div className="alert alert-danger">{messageError}
                        <button
                            type="button"
                            className="btn-close btn-sm position-absolute top-0 end-0 m-2"
                            aria-label="Close"
                            onClick={() => setMessageError(undefined)}
                        ></button>
                    </div>}
                </Col>
            </Row>
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                        <h2>{t("user:titleUser")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                    <Button
                        variant="primary"
                        disabled= {!hasClient}
                        style={{ whiteSpace: "nowrap" }}
                        onClick={
                            () => navigate(`/admin/users/new-user`)
                        }
                    >
                        {t("user:buttonNewUser")}
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <Search
                        searchText={searchTerm || ''}
                        onChange={(text) => setSearchTerm(text)}
                        onSearch={handleSearchInput}
                        onClear={handleClear}
                    />
                </Col>
            </Row>
            <Row>
                <Form>
                    <Form.Check
                        className="my-4"
                        type="switch"
                        id="custom-switch"
                        label={t("common:buttons.toggleActiveRecords")}
                        onChange={handleToggle}
                        checked={onlyActiveToggle}
                    />
                </Form>
            </Row>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                endDate={endDate}
                onSave={() => confirmSubmitModal()}
                isLoading={isLoading}
                handleMotiveDeleteChange={handleMotiveDeleteChange}
            />
            {hasClient? (
                <>
                    <AppTable<UserListFields>
                        data={usersData?.items || []}
                        columns={columns}
                        emptyMessage={"No records found"}
                        t={t}
                        hasActions={true}
                        hasPagination={true}
                        currentPage={currentPage}
                        totalPages={usersData?.totalPages || 0}
                        pageSize={itemsPerPage}
                        paginationType="roles"
                        sectionPermission={SectionPermissionTypeEnum.ROLES}
                        onView={(id:number) => {navigate(`/admin/users/${id}`) }}
                        canView={(user) => (currentUser.isSuperAdmin || !user.isSuperAdmin) && user.email != currentUser.email}
                        onExportCsv={() => { }}
                        onDelete={handleDelete}
                        canDelete={(user) => (currentUser.isSuperAdmin || !user.isSuperAdmin) && user.email != currentUser.email}
                        onRestore={handleRestore}
                        onActive={handleActive}
                    />
                </>
                ):(
                    <Row>
                        <Col xs={12} className="text-center">
                            <div className="alert alert-warning mt-4">
                                {t("user:systemAdminCantManageUsers")}
                            </div>
                        </Col>
                    </Row>
                )   
            }
            
        </>
    );
}
